import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Gift, Copy, Check, Share2, X } from "lucide-react";

const T = {
  ru: { title: "Приведи салон — получи месяц бесплатно", desc: "Поделитесь личной ссылкой. Когда приглашённый салон оплатит тариф, вы получите месяц Chatity бесплатно. Без ограничений по количеству.", copy: "Копировать", copied: "Скопировано", share: "Поделиться", signed: "Зарегистрировались", paid: "Оплатили", months: "Месяцев получено", shareText: "Я пользуюсь Chatity — AI-администратор для салона отвечает клиентам в WhatsApp и Instagram и записывает сам. Попробуй бесплатно:" },
  en: { title: "Refer a salon — get a month free", desc: "Share your personal link. When the salon you invited pays for a plan, you get one month of Chatity free. No limit.", copy: "Copy", copied: "Copied", share: "Share", signed: "Signed up", paid: "Paid", months: "Free months earned", shareText: "I use Chatity — an AI receptionist that answers clients on WhatsApp and Instagram and books them in. Try it free:" },
  fr: { title: "Parrainez un salon — un mois offert", desc: "Partagez votre lien personnel. Quand le salon invité paie un abonnement, vous recevez un mois de Chatity gratuit. Sans limite.", copy: "Copier", copied: "Copié", share: "Partager", signed: "Inscrits", paid: "Ont payé", months: "Mois offerts", shareText: "J'utilise Chatity — une réceptionniste IA qui répond aux clients sur WhatsApp et Instagram et prend les rendez-vous. Essayez gratuitement :" },
  tr: { title: "Bir salon getir — bir ay ücretsiz kazan", desc: "Kişisel bağlantınızı paylaşın. Davet ettiğiniz salon bir tarife ödediğinde bir ay Chatity ücretsiz. Sınır yok.", copy: "Kopyala", copied: "Kopyalandı", share: "Paylaş", signed: "Kayıt oldu", paid: "Ödedi", months: "Kazanılan ücretsiz ay", shareText: "Chatity kullanıyorum — WhatsApp ve Instagram'da müşterilere yanıt veren ve randevu alan AI resepsiyonist. Ücretsiz dene:" },
};

export default function ReferralBanner({ lang = "en", alwaysShow = false }) {
  const L = T[lang] || T.en;
  const [info, setInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  const [hidden, setHidden] = useState(() => !alwaysShow && localStorage.getItem("referral_banner_hidden") === "1");

  useEffect(() => {
    if (hidden) return;
    axios.get(`${API}/referral/me`).then((r) => setInfo(r.data)).catch(() => setInfo(null));
  }, [hidden]);

  if (hidden || !info?.enabled) return null;
  const link = `${window.location.origin}/?ref=${info.ref_code}`;
  const dismiss = () => { localStorage.setItem("referral_banner_hidden", "1"); setHidden(true); };

  const copy = async () => {
    await navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success(L.copied);
    setTimeout(() => setCopied(false), 2000);
  };
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Chatity", text: L.shareText, url: link }); } catch { /* cancelled */ }
    } else copy();
  };

  return (
    <div className="relative mb-4 md:mb-6 rounded-xl border-2 border-black bg-[#fff3c4] p-4 md:p-5 pr-10" data-testid="referral-banner">
      <button onClick={dismiss} aria-label="close" data-testid="referral-banner-close"
        className={`absolute top-2.5 right-2.5 w-7 h-7 rounded-full flex items-center justify-center text-[#57534a] hover:bg-black hover:text-white transition-colors ${alwaysShow ? "hidden" : ""}`}>
        <X className="w-4 h-4" />
      </button>
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        <div className="w-11 h-11 rounded-full bg-black flex items-center justify-center shrink-0">
          <Gift className="w-5 h-5 text-[#f5d76e]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-black text-base md:text-lg" data-testid="referral-banner-title">{L.title}</div>
          <div className="text-sm text-[#57534a] mt-0.5">{L.desc}</div>
          <div className="mt-3 flex flex-col sm:flex-row gap-2">
            <input readOnly value={link} onFocus={(e) => e.target.select()} data-testid="referral-link-input"
              className="flex-1 min-w-0 bg-white border-2 border-black rounded-lg px-3 py-2 text-sm font-mono text-black" />
            <button onClick={copy} data-testid="referral-copy-btn"
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-black text-white text-sm font-bold hover:bg-[#2a2823] transition-colors">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}{copied ? L.copied : L.copy}
            </button>
            <button onClick={share} data-testid="referral-share-btn"
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border-2 border-black text-black text-sm font-bold hover:bg-white transition-colors">
              <Share2 className="w-4 h-4" />{L.share}
            </button>
          </div>
        </div>
        <div className="flex flex-wrap lg:flex-col gap-x-4 gap-y-1 text-sm text-[#57534a] lg:text-right shrink-0" data-testid="referral-stats">
          <div><b className="text-black" data-testid="referral-signed-up">{info.signed_up}</b> {L.signed}</div>
          <div><b className="text-black" data-testid="referral-paid">{info.rewarded}</b> {L.paid}</div>
          <div><b className="text-black" data-testid="referral-free-months">{info.free_months}</b> {L.months}</div>
        </div>
      </div>
    </div>
  );
}
